import { InterfaceSettings } from '@zakeke/zakeke-customizer-react';
import { merge } from 'lodash';

export type ToolKey =
    | 'designElements'
    | 'printingMethods'
    | 'palette'
    | 'text'
    | 'textArt'
    | 'collages'
    | 'background'
    | 'drafts'
    | 'templates'
    | 'shapes'
    | 'uploadImage'
    | 'cliparts'
    | 'cart'
    | 'variants'
    // COMPOSER only — the configurator's entry point to the 2D design editor.
    | 'textAndImages';
export type ToolIconKey =
    | 'design-elements'
    | 'printing-methods'
    | 'switch-variant'
    | 'text'
    | 'textart'
    | 'collages'
    | 'background'
    | 'drafts'
    | 'templates'
    | 'shapes'
    | 'upload-image'
    | 'cliparts'
    | 'cart'
    | 'quote'
    | 'text-and-images';
// new configs
export type DesignElementsToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'designElements';
    longText?: string;
    position: number;
    text?: string;
};
export type TextToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'text';
    longText?: string;
    position: number;
    text?: string;
};
export type TextArtToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'textArt';
    longText?: string;
    position: number;
    text?: string;
};
export type ClipartToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'cliparts';
    longText?: string;
    position: number;
    text?: string;
    categoriesMode: boolean;
};
export type UploadImageToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'uploadImage';
    longText?: string;
    position: number;
    text?: string;
};
export type DraftsToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'drafts';
    longText?: string;
    text?: string;
};
export type TemplatesToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'templates';
    longText?: string;
    position: number;
    text?: string;
};
export type BackgroundToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'background';
    longText?: string;
    position: number;
    text?: string;
};
export type CollagesToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'collages';
    longText?: string;
    position: number;
    text?: string;
    visible: boolean;
};
export type ShapesToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'shapes';
    longText?: string;
    position: number;
    text?: string;
    visible: boolean;
};
export type PaletteToolConfig = {
    key: 'palette';
    visible: boolean;
};
// How the customizer lists variants that are not defined by multiple attributes, both in the variants panel and in
// the variant-selection dialogs. Must stay byte-for-byte in sync with the customizer: a missing key means 'gallery',
// so changing the default on one side only would turn the setting into a data migration.
export type VariantsDisplayMode = 'gallery' | 'list';
export const VARIANTS_DISPLAY_MODE_KEY = 'displayMode';
export const VARIANTS_DISPLAY_MODE_GALLERY = 'gallery';
export const VARIANTS_DISPLAY_MODE_LIST = 'list';

export type VariantsToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'variants';
    longText?: string;
    text?: string;
    visible: boolean;
    displayMode: VariantsDisplayMode;
};
export type PrintingMethodsToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'printingMethods';
    longText?: string;
    text?: string;
    visible: boolean;
};
export type CartToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'cart';
    longText?: string;
    text?: string;
};
export type QuoteToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultLongText: string;
    defaultText: string;
    iconUrl?: string;
    key: 'quote';
    longText?: string;
    text?: string;
};

/**
 * COMPOSER only. The configurator has a single entry point to the 2D design editor (the "Text & Images"
 * button in the side dock), so only its label and icon are configurable — it has no panel title of its
 * own, no position (it is not part of a toolbar) and no visibility toggle (it is shown whenever the
 * product has customizable areas). Omitting `defaultLongText`/`position`/`visible` is what makes the
 * generic <Tool> editor render just the two relevant controls.
 */
export type TextAndImagesToolConfig = {
    defaultIconKey: ToolIconKey;
    defaultText: string;
    iconUrl?: string;
    key: 'textAndImages';
    text?: string;
};

export type ToolsConfig = {
    designElements: DesignElementsToolConfig;
    uploadImage: UploadImageToolConfig;
    cliparts: ClipartToolConfig;
    text: TextToolConfig;
    textArt: TextArtToolConfig;
    background: BackgroundToolConfig;
    templates: TemplatesToolConfig;
    collages: CollagesToolConfig;
    shapes: ShapesToolConfig;
    palette: PaletteToolConfig;
    variants: VariantsToolConfig;
    printingMethods: PrintingMethodsToolConfig;
    drafts: DraftsToolConfig;
    cart: CartToolConfig;
    quote: QuoteToolConfig;
    textAndImages: TextAndImagesToolConfig;
};

export type FontFamilyConfig = {
    id: number;
    name: string;
    styleUrl: string;
    allTextUppercase: boolean;
};
export type ColorsConfig = {
    primary: string;
    primaryState: string;
    accent: string;
    accentState: string;
    cart?: string | null;
    cartState?: string | null;
    quote?: string | null;
    quoteState?: string | null;
    elementsOnPrimary: string;
    elementsOnAccent: string;
    elementsOnCart?: string | null;
    elementsOnQuote?: string | null;
    textOnlyAccent: string;
    textMain: string;
    textSecondary: string;
    textFooter: string;
    textDisabled: string;
    backgroundMain: string;
    backgroundSecondary: string;
    backgroundCanvas: string;
    backgroundFooter: string;
    background3d: string;
    backgroundPreview: string;
    borders: string;
    iconMain: string;
    iconSecondary: string;
    iconSecondaryState: string;
    iconDisabled: string;
    error: string;
    errorText: string;
    errorLight: string;
    success: string;
    scrollAlternative: string;
};
export type DesktopLayoutConfig = {
    footerPosition: 'top' | 'bottom';
    menuPosition: 'left' | 'right';
    toolbarWidth: number;
    addButtonVisible: boolean;
    addButtonOffsetX: number;
    addButtonOffsetY: number;
};

export type MobileLayoutConfig = {
    footerPosition: 'top' | 'bottom';
};

export type AccessibilityWidgetConfig = {
    buttonColor: string;
    buttonIconColor: string;
    hide: boolean;
};

/**
 * Controlli di interazione con la scena 3D (motivazione: accessibilità — rotazione senza mouse
 * e feedback visivo della parte selezionata). Attivi di default, il merchant può personalizzarli.
 * Consumati dal configuratore (COMPOSER). Da non confondere con `accessibilityWidget`,
 * che è il widget esterno a pagamento.
 */
export type Interaction3DConfig = {
    /** Colore dell'evidenziazione della parte 3D selezionata (feedback ipovedenti). */
    highlightColor: string;
    /** Mostra il pulsante di autorotazione (alternativa al drag). */
    showAutoRotateControl: boolean;
};

/**
 * Customizer behaviours that act as alternatives to mouse, touch and drag & drop.
 * They only apply to sellers owning the accessibility addon; the customizer treats a
 * missing key as `true`, so only the options turned off are ever persisted.
 */
export type AccessibilityConfig = {
    canvas3dControls: boolean;
    movableQuickActions: boolean;
    layersReorderButtons: boolean;
    itemTransformControls: boolean;
    nativeMobileSelect: boolean;
    panelsAutofocus: boolean;
};

export type CompositionRecapConfig = {
    isEnabled: boolean;
    isVisibleFromStart: boolean;
    canUserClose: boolean;
};

type DefaultCustomizerTheme = {
    fontFamily: FontFamilyConfig;
    colors: ColorsConfig;
    tools: ToolsConfig;
    desktopLayout: DesktopLayoutConfig;
    mobileLayout: MobileLayoutConfig;
    accessibilityWidget: AccessibilityWidgetConfig;
    interaction3D: Interaction3DConfig;
    accessibility: AccessibilityConfig;
    compositionRecap: CompositionRecapConfig;
};

type BackofficeInterfaceSettingsGeneral = {
    showDuplicateButton: boolean;
    showLayerButtons: boolean;
    showFlipButtons: boolean;
    enableManuallySizeImage: boolean;
};

export type DefaultCustomizerInterfaceSettings = InterfaceSettings<DefaultCustomizerTheme> & {
    general: InterfaceSettings<DefaultCustomizerTheme>['general'] & BackofficeInterfaceSettingsGeneral;
};

type DeepPartial<T> = T extends object
    ? {
          [P in keyof T]?: DeepPartial<T[P]>;
      }
    : T;

export type DefaultCustomizerInterfaceSettingsOverrides = DeepPartial<DefaultCustomizerInterfaceSettings>;

const removeNullValues = <T>(value: T): T => {
    if (Array.isArray(value)) {
        return value.map((item) => removeNullValues(item)).filter((item) => item !== null) as T;
    }
    if (value !== null && typeof value === 'object') {
        const result: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
            if (val === null) continue;
            result[key] = removeNullValues(val);
        }
        return result as T;
    }
    return value;
};

export const getUserInterfaceSettings = (
    settings: string | null | undefined,
    defaultSettings: DefaultCustomizerInterfaceSettings
): DefaultCustomizerInterfaceSettings => {
    if (!settings) {
        return defaultSettings;
    }
    return merge({}, defaultSettings, removeNullValues(JSON.parse(settings)));
};
